/**
 * Typed ALFIE API client.
 *
 * Design notes:
 * - One origin. In production the SPA is served by the backend; in dev Vite
 *   proxies `/api`. Either way requests are relative and the HttpOnly refresh
 *   cookie is sent automatically (`credentials: 'include'`).
 * - The access token lives in memory only (never localStorage). On a 401 the
 *   client transparently calls `/auth/refresh` once (deduping concurrent
 *   refreshes) and retries; if that fails it signals the auth layer to log out.
 * - Errors are normalized to {@link ApiClientError} carrying the RFC-7807 body.
 */
import type {
  ApiError,
  AuditEntry,
  AvailableModel,
  PullStatus,
  ChainResult,
  ChainSummary,
  ChainView,
  ChatMessageResponse,
  ChatStreamStatus,
  ChatRequest,
  ConsultResponse,
  Conversation,
  CreateModelRequest,
  CreateProviderRequest,
  DocSession,
  IntakeSummary,
  PrefillResponse,
  DocumentRevisionContent,
  DocumentRevisionView,
  UpcomingDeadline,
  DocType,
  DeconstructionSummary,
  DeconstructionView,
  DraftIssueView,
  DraftSessionDetail,
  DraftSessionSummary,
  FactDetail,
  FactDocument,
  FactSearchResponse,
  FormView,
  GeneratedDocument,
  HealthView,
  IngestionItem,
  IngestionJob,
  JobType,
  LayerAsset,
  LayerConfig,
  LayerView,
  ModelSummary,
  ModelView,
  ModelReadiness,
  Page,
  ProviderSummary,
  ProviderTestResult,
  ProviderView,
  Role,
  SourceView,
  SummarizeRequest,
  SummarizeSettings,
  SummaryResponse,
  AnalyzeRequest,
  AnalyzeSettings,
  AnalysisResponse,
  AnalyzerBuildRedlineResponse,
  AnalyzerIssueDecision,
  BriefAuditRequest,
  BriefAuditReport,
  ColloquyChangeSet,
  ColloquyConfigView,
  ColloquyReport,
  ColloquyReviewRequest,
  ExtractedText,
  ConnectorView,
  ConnectorRunView,
  GazetteDefaults,
  GazetteDeliveryRequest,
  GazetteDeliveryStatus,
  GazetteDeliveryView,
  GazetteEditionView,
  GazettePublicationRequest,
  GazettePublicationSummary,
  GazettePublicationView,
  GazetteGenerateRequest,
  GazetteNewsletterResponse,
  HeadnoteRequest,
  HeadnoteSettings,
  HeadnoteResponse,
  PrecedentRequest,
  PrecedentResult,
  PrecedentSetRequest,
  SavedPrecedentSet,
  DrafterSeedRequest,
  Citations,
  CitatorLookup,
  CitatorAnalyzeResponse,
  LibrarySourceView,
  AuthorityView,
  AuthorityResolution,
  AuthorityCitatorView,
  CitingPage,
  CitingContextsView,
  JurisdictionCount,
  ResearchDraftRequest,
  GoodLawSignal,
  TreatmentSummary,
  JobView,
  RedlineCompareInput,
  RedlineResponse,
  RedlineSide,
  DeadlineRuleView,
  DeadlinePackDetail,
  DeadlinePackUpsert,
  DeadlinePackView,
  DeadlineResult,
  ChronologyResponse,
  RedactionResponse,
  UsageSummary,
  LifetimeUsage,
  Matter,
  CreateMatterRequest,
  UpdateMatterRequest,
  MatterDocket,
  AttachDocketRequest,
  DocketEntriesPage,
  MatterDeadline,
  SyncResult,
  TemplateView,
  TokenResponse,
  UpdateModelRequest,
  UpdateProviderRequest,
  UserResponse,
  ValoremAdminField,
  ValoremAdminModel,
  ValoremAdminScenario,
  ValoremComparablesStats,
  ValoremCreateFieldRequest,
  ValoremCreateModelRequest,
  ValoremCreateProfileRequest,
  ValoremCreateScenarioRequest,
  ValoremCreateSourceRequest,
  ValoremCreateWeightProfileRequest,
  ValoremEvidenceRecord,
  ValoremEvidenceRun,
  ValoremEvidenceSource,
  ValoremImportRun,
  ValoremLocalCorpusStatus,
  ValoremModel,
  ValoremPrediction,
  ValoremPrefillSummary,
  ValoremProfile,
  ValoremReport,
  ValoremRun,
  ValoremRunDetail,
  ValoremRunLog,
  ValoremScenario,
  ValoremScenarioInput,
  ValoremSectionView,
  ValoremSettlementRecord,
  ValoremSourceTest,
  ValoremStartRunRequest,
  ValoremTraining,
  ValoremUpdateFieldRequest,
  ValoremUpdateModelRequest,
  ValoremUpdateProfileRequest,
  ValoremUpdateScenarioRequest,
  ValoremUpdateSourceRequest,
  ValoremUpdateWeightProfileRequest,
  ValoremWeightProfile,
  WatchView,
  NotificationView,
  ImpactScanView,
  VersionDiffView,
  LibraryHit,
  UnreadCount,
  CitationScanResponse,
  CitationCheck,
  OrgView,
  OrgsResponse,
  OrgMemberView,
  FirmBrainSearchResponse,
  FirmBrainStatus,
  ResearchCreateResponse,
  ResearchRunView,
  ResearchRunSummary,
  ServiceHealth,
  ServiceInfo,
  ChatAudience,
  RevisionDiff,
  DocumentCiteReport,
  OnboardingSummary,
  SampleMatter,
} from './types';
import { activity } from './activity';
import { logger } from './logger';

const BASE = '/api/v1';

/**
 * Every request and stream is logged here with its method, path, status,
 * duration and the backend's `X-Correlation-Id`. That transcript is what the
 * Diagnostics screen shows and what a support ticket should carry — see
 * `lib/logger.ts`.
 */
const log = logger.child('api');

// --------------------------------------------------------------- token state
let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function registerAuthLostHandler(fn: () => void): void {
  onAuthLost = fn;
}

/** A failed request, exposing the parsed problem document when present. */
export class ApiClientError extends Error {
  status: number;
  body?: ApiError;
  /**
   * The backend's `X-Correlation-Id` for the failed response, when present.
   * Surfaced in error states so a user can quote one string and an operator
   * can find the exact request in the server logs.
   */
  correlationId?: string;
  constructor(status: number, message: string, body?: ApiError, correlationId?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.body = body;
    this.correlationId = correlationId;
  }
}

/** `X-Correlation-Id` off a response, if the header survived the proxy. */
function correlationOf(res: Response): string | undefined {
  return res.headers?.get?.('X-Correlation-Id') ?? undefined;
}

/** An abort is a deliberate cancel (debounce, unmount) — never an error. */
function isAbort(e: unknown): boolean {
  return e instanceof DOMException ? e.name === 'AbortError' : (e as { name?: string })?.name === 'AbortError';
}

// ---------------------------------------------------------- refresh plumbing
let refreshInFlight: Promise<boolean> | null = null;

/**
 * The raw rotating-refresh network call, deduped within this tab. Never call this
 * directly — go through {@link refreshSession}, which also serializes across tabs.
 */
async function refreshNow(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = (await res.json()) as TokenResponse;
        accessToken = data.accessToken;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

let refreshLocked: Promise<boolean> | null = null;

/**
 * The single entry point for refreshing the access token — used by session restore
 * AND by the 401 retry paths in `request()` / `streamSse()`.
 *
 * The backend rotates the refresh token and treats REUSE as theft, revoking every
 * session. So two concurrent `/auth/refresh` calls carrying the same cookie do not
 * merely waste a round-trip — they hard-log the user out everywhere. Two guards:
 *
 *  - `refreshInFlight` collapses concurrent callers *within* a tab onto one call;
 *  - the `alfie-refresh` Web Lock SERIALIZES *across* tabs, so the second tab only
 *    refreshes after the first has finished and its cookie has rotated (a sequential
 *    rotation, which the backend accepts — not a reuse).
 *
 * Every refresh must acquire the lock here and nowhere else: a caller that takes the
 * lock and then calls a function that takes it again would deadlock. Falls back to the
 * in-process dedupe alone where Web Locks are unavailable (jsdom, older browsers).
 *
 * Resolves `true` when a fresh access token is in memory, `false` otherwise.
 */
export async function refreshSession(): Promise<boolean> {
  // Single-flight at this level as well: the Web Lock SERIALIZES concurrent
  // callers, so by the time the second one acquires the lock the first
  // refresh has finished and `refreshInFlight` is null again — without this
  // guard each serialized caller would perform its own network refresh.
  if (!refreshLocked) {
    const locks =
      typeof navigator !== 'undefined' &&
      typeof navigator.locks?.request === 'function'
        ? navigator.locks
        : null;
    // Promise.resolve flattens the nested promise the Web Locks typings produce
    // (LockManager.request<T> infers T = Promise<boolean> from the callback).
    refreshLocked = Promise.resolve(
      locks ? locks.request('alfie-refresh', () => refreshNow()) : refreshNow(),
    ).finally(() => {
      refreshLocked = null;
    });
  }
  return refreshLocked;
}

/**
 * Perform a silent session restore at app bootstrap. Alias of {@link refreshSession};
 * kept as its own name because that is what the call site means.
 */
export async function restoreSession(): Promise<boolean> {
  return refreshSession();
}

// --------------------------------------------------------------- core fetch
interface RequestOpts {
  method?: string;
  body?: unknown;
  /** When true, do not attempt a token refresh on 401 (used by auth calls). */
  noRetry?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
  raw?: boolean; // resolve with the Response (for blobs/text)
  /** Aborts the underlying fetch — for debounced calls superseded by newer input. */
  signal?: AbortSignal;
  /**
   * Address the server root instead of `/api/v1` — only Spring Boot's
   * `/actuator/health` and `/actuator/info`, which live outside the API base
   * and are the one public way to ask "is the backend up?".
   */
  root?: boolean;
}

function buildUrl(path: string, query?: RequestOpts['query'], root = false): string {
  const url = root ? path : `${BASE}${path}`;
  if (!query) return url;
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `${url}?${qs}` : url;
}

async function parseError(res: Response): Promise<ApiClientError> {
  let body: ApiError | undefined;
  try {
    body = (await res.json()) as ApiError;
  } catch {
    /* non-JSON error body */
  }
  const message = body?.detail || body?.title || `Request failed (${res.status})`;
  return new ApiClientError(res.status, message, body, correlationOf(res));
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const startedAt = Date.now();
  activity.requestStarted();
  try {
    const result = await performRequest<T>(path, opts, method, startedAt);
    activity.requestSucceeded(Date.now() - startedAt);
    return result;
  } catch (e) {
    if (isAbort(e)) {
      // A superseded request. It still has to leave the in-flight count.
      activity.requestSucceeded(Date.now() - startedAt);
      log.debug(`${method} ${path} aborted`);
      throw e;
    }
    if (e instanceof ApiClientError) {
      // The server answered — the transport is healthy even though we failed.
      activity.requestSucceeded(Date.now() - startedAt);
      const level = e.status >= 500 || e.status === 0 ? 'error' : 'warn';
      log[level](`${method} ${path} → ${e.status || 'no response'}`, {
        status: e.status,
        detail: e.message,
        correlationId: e.correlationId,
        ms: Date.now() - startedAt,
      });
    } else {
      const message = e instanceof Error ? e.message : 'Network error';
      activity.requestFailed(message);
      log.error(`${method} ${path} failed to reach the server`, { detail: message, ms: Date.now() - startedAt });
    }
    throw e;
  }
}

async function performRequest<T>(path: string, opts: RequestOpts, method: string, startedAt: number): Promise<T> {
  const doFetch = (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    let payload: BodyInit | undefined;
    if (opts.body instanceof FormData) {
      payload = opts.body;
    } else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(opts.body);
    }
    return fetch(buildUrl(path, opts.query, opts.root), {
      method,
      headers,
      body: payload,
      credentials: 'include',
      signal: opts.signal,
    });
  };

  let res = await doFetch();

  // Transparent single refresh-and-retry on expiry. Goes through refreshSession()
  // so it is serialized with every other tab — two tabs refreshing the same rotating
  // cookie at once reads as token theft and revokes all sessions.
  if (res.status === 401 && !opts.noRetry) {
    log.info('Access token expired — refreshing', { path });
    const refreshed = await refreshSession();
    log[refreshed ? 'info' : 'warn'](refreshed ? 'Session refreshed; retrying' : 'Refresh failed', { path });
    if (refreshed) res = await doFetch();
  }

  if (!res.ok) {
    // Fires once whether the refresh failed or the retried request still 401'd.
    if (res.status === 401 && !opts.noRetry) onAuthLost?.();
    throw await parseError(res);
  }

  log.debug(`${method} ${path} → ${res.status}`, {
    ms: Date.now() - startedAt,
    correlationId: correlationOf(res),
  });

  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    // A 2xx with a non-JSON body (e.g. a proxy error page) must surface as an
    // ApiClientError, not a raw SyntaxError.
    throw new ApiClientError(res.status, 'Malformed response from server', undefined, correlationOf(res));
  }
}

// ------------------------------------------------------------- SSE streaming
export interface StreamHandlers {
  onDelta: (text: string) => void;
  /** Called when the server emits an `event: error` frame (e.g. no engine configured). */
  onError?: (message: string) => void;
  /**
   * Real progress from the server (`event: status`): `retrieving` → `retrieved`
   * (with `authorities`/`documents` counts) → `generating`. Optional — older
   * callers that ignore it see no change.
   */
  onStatus?: (status: ChatStreamStatus) => void;
  signal?: AbortSignal;
}

/**
 * POSTs to an SSE endpoint and invokes `onDelta` for each token frame. The
 * backend sends raw token text as `data:` frames and a terminal `event: done`.
 * Resolves when the stream ends; rejects on transport/auth error.
 */
async function streamSse(path: string, body: unknown, handlers: StreamHandlers): Promise<void> {
  const startedAt = Date.now();
  let deltas = 0;
  let firstTokenAt: number | null = null;
  log.info(`SSE open ${path}`);
  const open = (): Promise<Response> => {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(buildUrl(path), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      credentials: 'include',
      signal: handlers.signal,
    });
  };

  let res = await open();
  if (res.status === 401) {
    const refreshed = await refreshSession();   // cross-tab serialized; see refreshSession()
    if (refreshed) res = await open();
    else {
      onAuthLost?.();
      throw new ApiClientError(401, 'Session expired');
    }
  }
  if (!res.ok || !res.body) {
    // Mirrors request(): a retried stream that still 401s means the session is gone.
    if (res.status === 401) onAuthLost?.();
    const err = await parseError(res);
    log.error(`SSE ${path} refused`, { status: err.status, detail: err.message, correlationId: err.correlationId });
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // SSE frames are separated by a blank line. Each frame may carry an `event:`
  // name and one or more `data:` lines (joined by newlines).
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    // Normalize CRLF line endings (a proxy may rewrite them) so frame and line
    // splitting below can assume '\n'. Re-normalizing the whole buffer also
    // handles a '\r\n' pair split across two transport chunks.
    buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let event = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
      }
      if (event === 'error') {
        const detail = dataLines.join('\n');
        log.error(`SSE ${path} error frame`, { detail, deltas, ms: Date.now() - startedAt });
        handlers.onError?.(detail || 'The assistant is unavailable right now.');
        return;
      }
      if (event === 'done') {
        log.info(`SSE ${path} complete`, { deltas, ttfbMs: firstTokenAt && firstTokenAt - startedAt, ms: Date.now() - startedAt });
        return;
      }
      if (event === 'status') {
        try {
          const status = JSON.parse(dataLines.join('\n')) as ChatStreamStatus;
          log.debug('SSE status', { stage: status.stage, authorities: status.authorities, documents: status.documents });
          handlers.onStatus?.(status);
        } catch { /* malformed status frames are informational only */ }
        continue;
      }
      if (dataLines.length) {
        if (firstTokenAt === null) firstTokenAt = Date.now();
        deltas += 1;
        handlers.onDelta(dataLines.join('\n'));
      }
    }
  }

  // The transport closed without a terminal `done`/`error` frame. The backend
  // may still have persisted a (partial) answer, but we cannot treat a dropped
  // connection as success — surface it so the caller can prompt a reload.
  log.error(`SSE ${path} dropped without a terminal frame`, { deltas, ms: Date.now() - startedAt });
  throw new ApiClientError(0, 'Connection lost — reload to see the saved answer.');
}

// ---------------------------------------------------------- multipart helper
/**
 * Build the multipart body used by every file-upload endpoint: each file under
 * the `files` field, plus any extra string fields. Blank/undefined extras are
 * dropped (the backend treats an absent field and an empty one the same way).
 */
/** A downloaded file: the body plus the server's Content-Disposition filename (or a fallback). */
async function attachment(res: Response, fallback: string): Promise<{ blob: Blob; filename: string }> {
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(cd);
  return { blob, filename: match ? match[1]! : fallback };
}

function filesToFormData(files: File[], extra: Record<string, string | undefined> = {}): FormData {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  for (const [key, value] of Object.entries(extra)) {
    if (value) fd.append(key, value);
  }
  return fd;
}

// =====================================================================
// Endpoint surface — grouped to mirror the backend controllers.
// =====================================================================
export const api = {
  auth: {
    register: (body: { email: string; username: string; password: string; displayName?: string }) =>
      request<UserResponse>('/auth/register', { method: 'POST', body, noRetry: true }),
    login: (body: { usernameOrEmail: string; password: string }) =>
      request<TokenResponse>('/auth/login', { method: 'POST', body, noRetry: true }),
    refresh: () => request<TokenResponse>('/auth/refresh', { method: 'POST', noRetry: true }),
    logout: () => request<void>('/auth/logout', { method: 'POST', noRetry: true }),
    me: () => request<UserResponse>('/auth/me'),
    changePassword: (body: { oldPassword: string; newPassword: string }) =>
      request<void>('/auth/change-password', { method: 'POST', body }),
    /** Redeem an email-verification token (from the link in the email). */
    verifyEmail: (token: string) =>
      request<void>('/auth/verify-email', { method: 'POST', body: { token }, noRetry: true }),
    /** Always 202 — the server never reveals whether the address exists. */
    resendVerification: (email: string) =>
      request<void>('/auth/resend-verification', { method: 'POST', body: { email }, noRetry: true }),
    /** Always 202 — the server never reveals whether the address exists. */
    forgotPassword: (email: string) =>
      request<void>('/auth/forgot-password', { method: 'POST', body: { email }, noRetry: true }),
    /** Redeem a password-reset token; 400 when the link is invalid/expired. */
    resetPassword: (token: string, newPassword: string) =>
      request<void>('/auth/reset-password', { method: 'POST', body: { token, newPassword }, noRetry: true }),
  },

  contact: {
    /**
     * Public contact form. `website` is a honeypot field: hidden from humans,
     * and submissions that fill it are silently dropped server-side.
     */
    send: (body: {
      name: string;
      email: string;
      subject?: string;
      message: string;
      category?: 'GENERAL' | 'SUPPORT' | 'ORDERS';
      website?: string;
    }) => request<void>('/contact', { method: 'POST', body, noRetry: true }),
  },

  users: {
    me: () => request<UserResponse>('/users/me'),
    get: (id: string) => request<UserResponse>(`/users/${id}`),
    updateProfile: (id: string, body: { displayName: string }) =>
      request<UserResponse>(`/users/${id}`, { method: 'PATCH', body }),
  },

  facts: {
    /**
     * Newest first. `filter: 'unfiled'` narrows to files filed under no matter; `matterId` lists
     * every file in that matter (any uploader). Neither: the caller's own uploads.
     */
    list: (page = 0, size = 20, opts: { filter?: 'unfiled'; matterId?: string } = {}) =>
      request<Page<FactDocument>>('/facts', { query: { page, size, filter: opts.filter, matterId: opts.matterId } }),
    upload: (files: File[]) =>
      request<FactDocument[]>('/facts', { method: 'POST', body: filesToFormData(files) }),
    remove: (id: string) => request<void>(`/facts/${id}`, { method: 'DELETE' }),
    /** One file opened: its row and the text ALFIE read from it. */
    get: (id: string) => request<FactDetail>(`/facts/${id}`),
    /** Index a failed file again from its stored bytes (uploader only). */
    retry: (id: string) => request<FactDocument>(`/facts/${id}/retry`, { method: 'POST' }),
    /** File under a matter, or unfile with `null` (uploader only). */
    file: (id: string, matterId: string | null) =>
      request<FactDocument>(`/facts/${id}/matter`, { method: 'PUT', body: { matterId } }),
    /** Hybrid search over the caller's record (own files + files in matters they can access). */
    search: (q: string, opts: { matterId?: string; limit?: number } = {}) =>
      request<FactSearchResponse>('/facts/search', { query: { q, matterId: opts.matterId, limit: opts.limit } }),
  },

  uploads: {
    /**
     * Upload instead of paste: a PDF/Word/text file's text (nothing is stored). `max` is the
     * calling tool's character cap — a longer file is refused with both numbers.
     */
    extract: (file: File, max?: number) => {
      const fd = new FormData();
      fd.append('file', file);
      if (max) fd.append('max', String(max));
      return request<ExtractedText>('/alfie/uploads/extract', { method: 'POST', body: fd });
    },
  },

  chat: {
    /**
     * Moves this browser's guest conversations into the signed-in account (called right after
     * sign-in/sign-up; the guest cookie is scoped to the chat path, so only a chat call carries it).
     */
    adoptGuest: () => request<{ adopted: number }>('/alfie/chat/guest/adopt', { method: 'POST' }),
    /** matterId files the conversation under a matter (signed-in only) so answers ground in its documents. */
    create: (title?: string, matterId?: string, audience?: ChatAudience) =>
      request<Conversation>('/alfie/chat/conversations', { method: 'POST', body: { title, matterId, audience } }),
    /** Partial update: rename and/or switch the audience (PROFESSIONAL | PLAIN); works for guests too. */
    update: (id: string, body: { title?: string; audience?: ChatAudience }) =>
      request<Conversation>(`/alfie/chat/conversations/${id}`, { method: 'PATCH', body }),
    list: (page = 0, size = 20, q?: string) =>
      request<Page<Conversation>>('/alfie/chat/conversations', { query: { page, size, q: q || undefined } }),
    rename: (id: string, title: string) =>
      request<Conversation>(`/alfie/chat/conversations/${id}`, { method: 'PATCH', body: { title } }),
    /** Suggested next questions for one answer (one small model call, cached on the message). */
    followups: (id: string, messageId: string) =>
      request<{ questions: string[] }>(`/alfie/chat/conversations/${id}/messages/${messageId}/followups`, { method: 'POST' }),
    /** Save one answer as a new editable document (signed-in only); filed under the conversation's matter. */
    saveAsDocument: (id: string, messageId: string, title?: string) =>
      request<GeneratedDocument>(`/alfie/chat/conversations/${id}/messages/${messageId}/document`,
        { method: 'POST', body: { title } }),
    /** Fork a conversation at a message: the branch carries the messages up to and including it. */
    branch: (id: string, messageId: string) =>
      request<Conversation>(`/alfie/chat/conversations/${id}/branch`, { method: 'POST', body: { messageId } }),
    remove: (id: string) => request<void>(`/alfie/chat/conversations/${id}`, { method: 'DELETE' }),
    messages: (id: string) =>
      request<ChatMessageResponse[]>(`/alfie/chat/conversations/${id}/messages`),
    send: (id: string, body: ChatRequest) =>
      request<ChatMessageResponse>(`/alfie/chat/conversations/${id}/messages`, { method: 'POST', body }),
    stream: (id: string, body: ChatRequest, handlers: StreamHandlers) =>
      streamSse(`/alfie/chat/conversations/${id}/stream`, body, handlers),
    /** Regenerate the reply to the conversation's last user message (SSE, like stream). */
    regenerate: (id: string, body: { providerKey?: string; layerOverrides?: Record<string, boolean> }, handlers: StreamHandlers) =>
      streamSse(`/alfie/chat/conversations/${id}/regenerate`, body, handlers),
    /** Download one assistant message as a file (works for guests too). */
    exportMessage: async (id: string, messageId: string, format: 'MARKDOWN' | 'TXT' | 'PDF' | 'DOCX') => {
      const res = await request<Response>(
        `/alfie/chat/conversations/${id}/messages/${messageId}/export`,
        { raw: true, query: { format } },
      );
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      return { blob, filename: match ? match[1]! : `answer.${format.toLowerCase()}` };
    },
    /** Attach documents to a conversation ("chat with a document"). */
    attachDocuments: (id: string, files: File[]) =>
      request<FactDocument[]>(`/alfie/chat/conversations/${id}/documents`, { method: 'POST', body: filesToFormData(files) }),
    /** Attach files already in ALFIE by id ("Ask ALFIE about this") — no re-upload. */
    attachExisting: (id: string, factDocumentIds: string[]) =>
      request<FactDocument[]>(`/alfie/chat/conversations/${id}/documents/attach`,
        { method: 'POST', body: { factDocumentIds } }),
    attachedDocuments: (id: string) =>
      request<FactDocument[]>(`/alfie/chat/conversations/${id}/documents`),
    detachDocument: (id: string, documentId: string) =>
      request<void>(`/alfie/chat/conversations/${id}/documents/${documentId}`, { method: 'DELETE' }),
  },

  docgen: {
    docTypes: () => request<DocType[]>('/alfie/docgenerator/doc-types'),
    /** `matterId` files the eventual document under a matter (and grounds it in the matter's case files). */
    start: (formKey: string, matterId?: string) =>
      request<DocSession>('/alfie/docgenerator/sessions', { method: 'POST', body: { formKey, matterId } }),
    session: (id: string) => request<DocSession>(`/alfie/docgenerator/sessions/${id}`),
    /** Unfinished intakes (answered, not yet generated), most recently touched first. */
    sessions: (page = 0, size = 10) =>
      request<Page<IntakeSummary>>('/alfie/docgenerator/sessions', { query: { page, size } }),
    discard: (id: string) => request<void>(`/alfie/docgenerator/sessions/${id}`, { method: 'DELETE' }),
    /** Merge answers; a field sent as `null` is cleared. */
    answers: (id: string, answers: Record<string, unknown>) =>
      request<DocSession>(`/alfie/docgenerator/sessions/${id}/answers`, { method: 'POST', body: { answers } }),
    /** Fill the intake from the user's own description; their typed answers are never overwritten. */
    prefill: (id: string, description: string) =>
      request<PrefillResponse>(`/alfie/docgenerator/sessions/${id}/prefill`, { method: 'POST', body: { description } }),
    /** File (or with `null`, un-file) the eventual document under a matter. */
    setMatter: (id: string, matterId: string | null) =>
      request<DocSession>(`/alfie/docgenerator/sessions/${id}/matter`, { method: 'PUT', body: { matterId } }),
    consult: (id: string, message: string) =>
      request<ConsultResponse>(`/alfie/docgenerator/sessions/${id}/consult`, { method: 'POST', body: { message } }),
    generate: (id: string, body: { templateKey?: string; title?: string; finalize: boolean }) =>
      request<GeneratedDocument>(`/alfie/docgenerator/sessions/${id}/generate`, { method: 'POST', body }),
    /** Generation on the durable lane — poll the job; it reports each stage, and `resultDocumentId` is the document. */
    generateAsync: (id: string, body: { templateKey?: string; title?: string; finalize: boolean }) =>
      request<JobView>(`/alfie/docgenerator/sessions/${id}/generate/async`, { method: 'POST', body }),
    documents: (page = 0, size = 20) =>
      request<Page<GeneratedDocument>>('/alfie/docgenerator/documents', { query: { page, size } }),
    document: (id: string) => request<GeneratedDocument>(`/alfie/docgenerator/documents/${id}`),
    /** Save an edit; pass the revision you loaded so a colleague's newer save yields a 409 instead of being overwritten. */
    updateDocument: (id: string, body: { documentContent: string; baseRevision?: number; note?: string }) =>
      request<GeneratedDocument>(`/alfie/docgenerator/documents/${id}`, { method: 'PUT', body }),
    revisions: (id: string) => request<DocumentRevisionView[]>(`/alfie/docgenerator/documents/${id}/revisions`),
    revision: (id: string, revisionNo: number) =>
      request<DocumentRevisionContent>(`/alfie/docgenerator/documents/${id}/revisions/${revisionNo}`),
    restoreRevision: (id: string, revisionNo: number) =>
      request<GeneratedDocument>(`/alfie/docgenerator/documents/${id}/revisions/${revisionNo}/restore`, { method: 'POST' }),
    /** Redline from a revision to the current text (or to `against`, another revision number). */
    revisionDiff: (id: string, revisionNo: number, against?: number) =>
      request<RevisionDiff>(`/alfie/docgenerator/documents/${id}/revisions/${revisionNo}/diff`, { query: { against } }),
    exportMarkdown: (id: string) =>
      request<Response>(`/alfie/docgenerator/documents/${id}/export`, { raw: true, query: { format: 'MARKDOWN', part: 'ALL' } }).then((r) => r.text()),
    /** Style packs available for PDF/DOCX export (court-filing, contract, plain, …). */
    exportStyles: () => request<string[]>('/alfie/docgenerator/export-styles'),
    /** Download a rendered file; resolves the server-provided filename from the response. */
    export: async (id: string, format: 'MARKDOWN' | 'PDF' | 'DOCX', part: 'DOCUMENT' | 'GUIDE' | 'CHECKLIST' | 'ALL', style: string) => {
      const res = await request<Response>(`/alfie/docgenerator/documents/${id}/export`,
        { raw: true, query: { format, part, style } });
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      return { blob, filename: match ? match[1]! : `document.${format.toLowerCase()}` };
    },
  },

  summarizer: {
    summarize: (body: SummarizeRequest) =>
      request<SummaryResponse>('/alfie/summarizer', { method: 'POST', body }),
    /** Summarize uploaded documents: one summary per file, or one combined summary. */
    summarizeDocuments: (files: File[], mode: 'PER_DOCUMENT' | 'COMBINED', settings: SummarizeSettings = {}) => {
      const fd = filesToFormData(files, {
        mode,
        length: settings.length,
        tone: settings.tone,
        format: settings.format,
        sentences: settings.sentences != null ? String(settings.sentences) : undefined,
        focus: settings.focus,
        audience: settings.audience,
        useFacts: settings.useFacts ? 'true' : undefined,
        title: settings.title,
      });
      return request<SummaryResponse[]>('/alfie/summarizer/documents', { method: 'POST', body: fd });
    },
    history: (page = 0, size = 20) =>
      request<Page<SummaryResponse>>('/alfie/summarizer/summaries', { query: { page, size } }),
  },

  analyzer: {
    analyze: (body: AnalyzeRequest) =>
      request<AnalysisResponse>('/alfie/analyzer', { method: 'POST', body }),
    /** Analyze uploaded documents: one report per file, or one combined report. */
    analyzeDocuments: (files: File[], mode: 'PER_DOCUMENT' | 'COMBINED', settings: AnalyzeSettings = {}) => {
      const fd = filesToFormData(files, {
        mode,
        focus: settings.focus,
        useFacts: settings.useFacts ? 'true' : undefined,
        title: settings.title,
      });
      return request<AnalysisResponse[]>('/alfie/analyzer/documents', { method: 'POST', body: fd });
    },
    history: (page = 0, size = 20) =>
      request<Page<AnalysisResponse>>('/alfie/analyzer/analyses', { query: { page, size } }),
    /** One analysis with its issues (owner or org member). */
    get: (id: string) => request<AnalysisResponse>(`/alfie/analyzer/analyses/${id}`),
    /** Analyze pasted text in the background; returns a job to poll. */
    analyzeAsync: (body: AnalyzeRequest) =>
      request<JobView>('/alfie/analyzer/async', { method: 'POST', body }),
    /** Analyze uploaded documents in the background; returns a job to poll. */
    analyzeDocumentsAsync: (files: File[], settings: AnalyzeSettings = {}) => {
      const fd = filesToFormData(files, {
        focus: settings.focus,
        useFacts: settings.useFacts ? 'true' : undefined,
        title: settings.title,
        matterId: settings.matterId,
      });
      return request<JobView>('/alfie/analyzer/documents/async', { method: 'POST', body: fd });
    },
    /** Apply the accepted suggestions to the analyzed text; returns the marked-up contract (a redline). */
    buildRedline: (id: string, decisions: AnalyzerIssueDecision[], title?: string) =>
      request<AnalyzerBuildRedlineResponse>(`/alfie/analyzer/analyses/${id}/redline`,
        { method: 'POST', body: { decisions, title } }),
  },

  precedent: {
    /** Find on-point cases in the research library for a fact pattern or issue (each with its good-law flag). */
    find: (body: PrecedentRequest) =>
      request<PrecedentResult[]>('/alfie/precedent', { method: 'POST', body }),
    /** Save selected results as a precedent-set document (editable, exportable, matter-filed). */
    saveSet: (body: PrecedentSetRequest) =>
      request<SavedPrecedentSet>('/alfie/precedent/sets', { method: 'POST', body }),
  },

  // The case reader: open any library authority, and the jurisdictions the library holds.
  library: {
    source: (id: string) => request<LibrarySourceView>(`/alfie/library/sources/${id}`),
    jurisdictions: () => request<JurisdictionCount[]>('/alfie/library/jurisdictions'),
  },

  // Authorities by key (ADR 0011: UpLaw is the law library) — `uplaw:<did>`, or a legacy local id.
  authorities: {
    get: (key: string) => request<AuthorityView>(`/alfie/authorities/${encodeURIComponent(key)}`),
    /** Resolve a citation to an authority: FOUND / NOT_FOUND / UNKNOWN, its key and good law. */
    resolve: (citation: string) =>
      request<AuthorityResolution>('/alfie/authorities/resolve', { query: { citation } }),
    /** The law library's citator report: flag + reasons, history, negative treatment, first citing refs. */
    citator: (key: string, citing = 25) =>
      request<AuthorityCitatorView>(`/alfie/authorities/${encodeURIComponent(key)}/citator`, { query: { citing } }),
    /** A page of the documents citing an UpLaw authority. */
    citedBy: (key: string, page = 0, size = 20) =>
      request<CitingPage>(`/alfie/authorities/${encodeURIComponent(key)}/cited-by`, { query: { page, size } }),
    /**
     * The sentences a page of citing documents cite the authority in — asked after the page
     * renders; empty until UpLaw serves them (ADR 0011 step 3).
     */
    citingContexts: (key: string, citing: string[], perRef = 2) =>
      request<CitingContextsView>(`/alfie/authorities/${encodeURIComponent(key)}/citing-contexts`, {
        method: 'POST', body: { citing, perRef },
      }),
  },

  citator: {
    /** The citation graph for a library source: the cases it cites and the ones that cite it. */
    citations: (sourceId: string, page = 0, size = 20) =>
      request<Citations>(`/alfie/citator/sources/${sourceId}/citations`, { query: { page, size } }),
    /** Classify (via the AI) how this source treats each case it cites; returns the count classified. */
    classify: (sourceId: string) =>
      request<{ classified: number }>(`/alfie/citator/sources/${sourceId}/classify`, { method: 'POST' }),
    /** Look up any citation (e.g. "477 U.S. 317"): who cites it, and what it cites if it's in the library. */
    lookup: (q: string, page = 0, size = 20) =>
      request<CitatorLookup>('/alfie/citator/lookup', { query: { q, page, size } }),
    /** Aggregate treatment for a source: how many opinions cite it and how they treat it. */
    treatmentSummary: (sourceId: string) =>
      request<TreatmentSummary>(`/alfie/citator/sources/${sourceId}/treatment-summary`),
    /** The KeyCite-style good-law flag (direct + transitive) for any citation. */
    goodLaw: (q: string) =>
      request<GoodLawSignal>('/alfie/citator/good-law', { query: { q } }),
    /** Classify a batch of this case's unanalyzed citing opinions (a background job; `job` null when none remain). */
    analyze: (q: string) =>
      request<CitatorAnalyzeResponse>('/alfie/citator/analyze', { method: 'POST', query: { q } }),
  },

  briefAudit: {
    /** Audit the citations in a brief: existence (fabrication), good-law status, and quote fidelity. */
    audit: (body: BriefAuditRequest) =>
      request<BriefAuditReport>('/alfie/brief-audit', { method: 'POST', body }),
  },

  headnote: {
    /** Generate headnotes from pasted legal text (usually a judicial opinion). */
    generate: (body: HeadnoteRequest) =>
      request<HeadnoteResponse>('/alfie/headnote', { method: 'POST', body }),
    /** Generate headnotes from one or more uploaded documents (combined). */
    generateFromDocuments: (files: File[], settings: HeadnoteSettings = {}) => {
      const fd = filesToFormData(files, {
        focus: settings.focus,
        useFacts: settings.useFacts ? 'true' : undefined,
        generateAnyway: settings.generateAnyway ? 'true' : undefined,
        title: settings.title,
      });
      return request<HeadnoteResponse>('/alfie/headnote/documents', { method: 'POST', body: fd });
    },
    history: (page = 0, size = 20) =>
      request<Page<HeadnoteResponse>>('/alfie/headnote/headnotes', { query: { page, size } }),
    /** Generate headnotes from uploaded documents in the background; returns a job to poll. */
    generateFromDocumentsAsync: (files: File[], settings: HeadnoteSettings = {}) => {
      const fd = filesToFormData(files, {
        focus: settings.focus,
        useFacts: settings.useFacts ? 'true' : undefined,
        generateAnyway: settings.generateAnyway ? 'true' : undefined,
        title: settings.title,
      });
      return request<JobView>('/alfie/headnote/documents/async', { method: 'POST', body: fd });
    },
  },

  /** Gazette — AI newsletter generator. */
  gazette: {
    /** Generate a newsletter synchronously (small requests only). */
    generate: (body: GazetteGenerateRequest) =>
      request<GazetteNewsletterResponse>('/alfie/gazette/newsletters', { method: 'POST', body }),
    /** Generate a newsletter in the background; returns a job to poll. */
    generateAsync: (body: GazetteGenerateRequest) =>
      request<JobView>('/alfie/gazette/newsletters/async', { method: 'POST', body }),
    history: (page = 0, size = 20) =>
      request<Page<GazetteNewsletterResponse>>('/alfie/gazette/newsletters', { query: { page, size } }),
    get: (id: string) => request<GazetteNewsletterResponse>(`/alfie/gazette/newsletters/${id}`),
    /** Download a rendered edition; resolves the server-provided filename from the response. */
    file: async (id: string, format: 'html' | 'md' | 'json') => {
      const res = await request<Response>(`/alfie/gazette/newsletters/${id}/file`, { raw: true, query: { format } });
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      return { blob, filename: match ? match[1]! : `newsletter.${format}` };
    },
    /** Intake defaults: section plan, word budget, tones, colloquy configs. */
    defaults: () => request<GazetteDefaults>('/alfie/gazette/defaults'),

    // Saved publications (recurring newsletters with a list and a schedule).
    publications: (page = 0, size = 50) =>
      request<Page<GazettePublicationSummary>>('/alfie/gazette/publications', { query: { page, size } }),
    publication: (id: string) => request<GazettePublicationView>(`/alfie/gazette/publications/${id}`),
    createPublication: (body: GazettePublicationRequest) =>
      request<GazettePublicationView>('/alfie/gazette/publications', { method: 'POST', body }),
    updatePublication: (id: string, body: GazettePublicationRequest) =>
      request<GazettePublicationView>(`/alfie/gazette/publications/${id}`, { method: 'PUT', body }),
    deletePublication: (id: string) =>
      request<void>(`/alfie/gazette/publications/${id}`, { method: 'DELETE' }),
    /** Write an issue of the publication now; returns a job whose result is the issue. Never sent automatically. */
    generateEdition: (id: string) =>
      request<JobView>(`/alfie/gazette/publications/${id}/editions`, { method: 'POST' }),
    editions: (id: string, page = 0, size = 10) =>
      request<Page<GazetteEditionView>>(`/alfie/gazette/publications/${id}/editions`, { query: { page, size } }),

    // Email delivery.
    deliveryStatus: () => request<GazetteDeliveryStatus>('/alfie/gazette/delivery-status'),
    /** `test` sends only to you, right away; otherwise the list is sent in the background (poll the delivery). */
    send: (newsletterId: string, body: GazetteDeliveryRequest) =>
      request<GazetteDeliveryView>(`/alfie/gazette/newsletters/${newsletterId}/deliveries`, { method: 'POST', body }),
    deliveries: (newsletterId: string) =>
      request<GazetteDeliveryView[]>(`/alfie/gazette/newsletters/${newsletterId}/deliveries`),
    delivery: (deliveryId: string) => request<GazetteDeliveryView>(`/alfie/gazette/deliveries/${deliveryId}`),
  },

  /** Colloquy — multi-AI peer review of a draft text. */
  colloquy: {
    /** Run a peer review synchronously (small drafts only). */
    review: (body: ColloquyReviewRequest) =>
      request<ColloquyReport>('/alfie/colloquy/reviews', { method: 'POST', body }),
    /** Run a peer review in the background; returns a job to poll. */
    reviewAsync: (body: ColloquyReviewRequest) =>
      request<JobView>('/alfie/colloquy/reviews/async', { method: 'POST', body }),
    history: (page = 0, size = 20) =>
      request<Page<ColloquyReport>>('/alfie/colloquy/reviews', { query: { page, size } }),
    get: (id: string) => request<ColloquyReport>(`/alfie/colloquy/reviews/${id}`),
    /** Original → final as numbered changes to accept or reject one by one. */
    changes: (id: string) => request<ColloquyChangeSet>(`/alfie/colloquy/reviews/${id}/changes`),
    /** Read an uploaded PDF/DOCX into text (nothing is stored). */
    extract: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return request<ExtractedText>('/alfie/colloquy/extract', { method: 'POST', body: fd });
    },
    /** The configured review panels (author + reviewer roster), with DEGRADED state. */
    configs: () => request<ColloquyConfigView[]>('/alfie/colloquy/configs'),
  },

  jobs: {
    get: (id: string) => request<JobView>(`/alfie/jobs/${id}`),
    list: () => request<JobView[]>('/alfie/jobs'),
    /**
     * Poll a background job until it finishes (or the attempt budget is
     * exhausted). Pass an `AbortSignal` to stop polling — e.g. from a page's
     * unmount cleanup — in which case the promise rejects with an `AbortError`
     * DOMException (callers should swallow it rather than surface an error).
     */
    poll: async (
      id: string,
      { intervalMs = 1500, maxAttempts = 160, signal, onProgress }: {
        intervalMs?: number; maxAttempts?: number; signal?: AbortSignal;
        /** Called on every poll while the job is still running — carries the job's reported stage. */
        onProgress?: (job: JobView) => void;
      } = {},
    ): Promise<JobView> => {
      const abortError = () => new DOMException('Job polling aborted', 'AbortError');
      const jobLog = log.child('job');
      const startedAt = Date.now();
      let lastStage: string | undefined;
      jobLog.info('Polling started', { jobId: id });
      for (let i = 0; i < maxAttempts; i++) {
        if (signal?.aborted) throw abortError();
        const job = await request<JobView>(`/alfie/jobs/${id}`);
        if (job.state === 'COMPLETE' || job.state === 'FAILED') {
          jobLog[job.state === 'FAILED' ? 'error' : 'info'](`Job ${job.state.toLowerCase()}`, {
            jobId: id, tool: job.tool, polls: i + 1, ms: Date.now() - startedAt,
          });
          return job;
        }
        // Log each *change* of stage, not each poll — otherwise a ten-minute
        // research run buries everything else in the buffer.
        if (job.progress && job.progress !== lastStage) {
          lastStage = job.progress;
          jobLog.info(`Job stage: ${job.progress}`, { jobId: id, state: job.state });
        }
        onProgress?.(job);
        await new Promise<void>((resolve, reject) => {
          const onAbort = () => {
            clearTimeout(timer);
            reject(abortError());
          };
          const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
          }, intervalMs);
          signal?.addEventListener('abort', onAbort, { once: true });
        });
      }
      jobLog.warn('Polling gave up', { jobId: id, maxAttempts, ms: Date.now() - startedAt });
      throw new Error('The operation is taking longer than expected. Check back shortly.');
    },
  },

  redline: {
    /** Compare any mix of upload / pasted text / ALFIE document per side. */
    compareInputs: (input: RedlineCompareInput) => {
      const fd = new FormData();
      const side = (name: 'original' | 'revised', s: RedlineSide) => {
        if (s.kind === 'file' && s.file) fd.append(name, s.file);
        if (s.kind === 'text') fd.append(`${name}Text`, s.text);
        if (s.kind === 'document') {
          fd.append(`${name}DocumentId`, s.documentId);
          if (s.revision !== null) fd.append(`${name}Revision`, String(s.revision));
        }
      };
      side('original', input.original);
      side('revised', input.revised);
      if (input.focus) fd.append('focus', input.focus);
      if (input.title) fd.append('title', input.title);
      if (input.matterId) fd.append('matterId', input.matterId);
      if (input.asyncSummary) fd.append('asyncSummary', 'true');
      return request<RedlineResponse>('/alfie/redline/documents', { method: 'POST', body: fd });
    },
    history: (page = 0, size = 20) =>
      request<Page<RedlineResponse>>('/alfie/redline/redlines', { query: { page, size } }),
    /** One redline with its numbered changes (owner or org member). */
    get: (id: string) => request<RedlineResponse>(`/alfie/redline/redlines/${id}`),
    /** The comparison as a Word file with real tracked changes. */
    trackedChanges: async (id: string) => {
      const res = await request<Response>(`/alfie/redline/redlines/${id}/tracked-changes`, { raw: true });
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      return { blob, filename: match ? match[1]! : 'redline-tracked-changes.docx' };
    },
  },

  usage: {
    summary: (days = 30) => request<UsageSummary>('/usage/summary', { query: { days } }),
    lifetime: () => request<LifetimeUsage>('/usage/lifetime'),
  },

  // Live cite-check (roadmap §3.8): deterministic span scan + composed
  // per-citation verdict for the citation-aware editor.
  citations: {
    scan: (text: string, signal?: AbortSignal) =>
      request<CitationScanResponse>('/alfie/citations/scan', { method: 'POST', body: { text }, signal }),
    check: (citation: string, quote?: string) =>
      request<CitationCheck>('/alfie/citations/check', { method: 'POST', body: { citation, quote } }),
    /** Save an editor draft as a document (tool `citecheck`); later saves use docgen.updateDocument. */
    saveDraft: (body: { title?: string; text: string; matterId?: string }) =>
      request<GeneratedDocument>('/alfie/citations/documents', { method: 'POST', body }),
    /** Cite-check one part of a saved document (DOCUMENT · GUIDE · CHECKLIST): every citation, verdicts attached. */
    documentReport: (documentId: string, part: 'DOCUMENT' | 'GUIDE' | 'CHECKLIST' = 'DOCUMENT') =>
      request<DocumentCiteReport>(`/alfie/citations/documents/${documentId}/report`, { method: 'POST', query: { part } }),
  },

  // Statute tracker (roadmap §3.6): watches on library sources + the
  // notification inbox their change alerts land in.
  watches: {
    list: (page = 0, size = 20) =>
      request<Page<WatchView>>('/alfie/watches', { query: { page, size } }),
    /** Ids of every source the user watches, for marking toggles in list UIs. */
    sourceIds: () => request<string[]>('/alfie/watches/source-ids'),
    create: (body: { sourceId: string; matterId?: string }) =>
      request<WatchView>('/alfie/watches', { method: 'POST', body }),
    remove: (id: string) => request<void>(`/alfie/watches/${id}`, { method: 'DELETE' }),
    removeBySource: (sourceId: string) =>
      request<void>(`/alfie/watches/by-source/${sourceId}`, { method: 'DELETE' }),
    /** Impact-scan history for one watched source (roadmap §3.14), newest first. */
    impacts: (sourceId: string) =>
      request<ImpactScanView[]>(`/alfie/watches/${sourceId}/impacts`),
    /** Authorities to watch, by citation or title (≥3 characters; ≤10 hits, each flagged when watched). */
    search: (q: string, signal?: AbortSignal) =>
      request<LibraryHit[]>('/alfie/watches/search', { query: { q }, signal }),
    /** What changed in one version of an authority (the alert page's redline). */
    versionDiff: (sourceId: string, versionNo: number) =>
      request<VersionDiffView>(`/alfie/watches/${sourceId}/versions/${versionNo}/diff`),
  },

  /**
   * Liveness, outside the API base path. `/actuator/health` is the only
   * unauthenticated way to ask whether the backend is actually up — the System
   * Status screen and the offline strip both lean on it. Detail components are
   * only returned to an authorised caller (`show-details: when-authorized`), so
   * an anonymous visitor sees the overall status and nothing more.
   */
  system: {
    health: (signal?: AbortSignal) => request<ServiceHealth>('/actuator/health', { root: true, signal, noRetry: true }),
    info: () => request<ServiceInfo>('/actuator/info', { root: true, noRetry: true }),
  },

  notifications: {
    list: (page = 0, size = 20, unread = false) =>
      request<Page<NotificationView>>('/alfie/notifications', { query: { page, size, unread } }),
    /** One notification (the alert page); someone else's is 404. */
    get: (id: string) => request<NotificationView>(`/alfie/notifications/${id}`),
    unreadCount: () => request<UnreadCount>('/alfie/notifications/unread-count'),
    markRead: (id: string) => request<void>(`/alfie/notifications/${id}/read`, { method: 'POST' }),
    markAllRead: () => request<void>('/alfie/notifications/read-all', { method: 'POST' }),
  },

  // Organizations & teams (roadmap §6.4): personal + shared orgs, membership,
  // and active-org switching. Activation re-mints the access token — install
  // the returned token via setAccessToken so the new org context applies.
  orgs: {
    list: () => request<OrgsResponse>('/alfie/orgs'),
    create: (name: string) =>
      request<OrgView>('/alfie/orgs', { method: 'POST', body: { name } }),
    rename: (id: string, name: string) =>
      request<OrgView>(`/alfie/orgs/${id}`, { method: 'PATCH', body: { name } }),
    remove: (id: string) => request<void>(`/alfie/orgs/${id}`, { method: 'DELETE' }),
    members: (id: string) => request<OrgMemberView[]>(`/alfie/orgs/${id}/members`),
    addMember: (id: string, usernameOrEmail: string, role: 'ORG_ADMIN' | 'MEMBER') =>
      request<OrgMemberView>(`/alfie/orgs/${id}/members`, { method: 'POST', body: { usernameOrEmail, role } }),
    updateMember: (id: string, userId: string, role: 'ORG_ADMIN' | 'MEMBER') =>
      request<OrgMemberView>(`/alfie/orgs/${id}/members/${userId}`, { method: 'PATCH', body: { role } }),
    removeMember: (id: string, userId: string) =>
      request<void>(`/alfie/orgs/${id}/members/${userId}`, { method: 'DELETE' }),
    activate: (id: string) =>
      request<TokenResponse>(`/alfie/orgs/${id}/activate`, { method: 'POST' }),
  },

  // Agentic research (roadmap §3.9): a bounded plan → retrieve/expand →
  // assess → synthesize loop on the durable job lane. Poll the run for the
  // live trail; the memo lands as a generated document.
  research: {
    start: (body: { question: string; jurisdiction?: string; matterId?: string; maxSteps?: number }) =>
      request<ResearchCreateResponse>('/alfie/research', { method: 'POST', body }),
    get: (id: string, signal?: AbortSignal) =>
      request<ResearchRunView>(`/alfie/research/runs/${id}`, { signal }),
    list: (page = 0, size = 20, matterId?: string) =>
      request<Page<ResearchRunSummary>>('/alfie/research/runs', { query: { page, size, matterId } }),
    /** "Draft from this research": a drafter session in the run's matter with its vetted authorities pinned. */
    draft: (id: string, body: ResearchDraftRequest = {}) =>
      request<DraftSessionDetail>(`/alfie/research/runs/${id}/draft`, { method: 'POST', body }),
  },

  // Firm Brain (roadmap §3.16): org-scoped hybrid search over the firm's own
  // generated work product, plus the "briefed before?" citation check.
  firmbrain: {
    search: (body: { query: string; topK?: number; tool?: string; orgId?: string }) =>
      request<FirmBrainSearchResponse>('/alfie/firmbrain/search', { method: 'POST', body }),
    briefedBefore: (body: { issueText: string; citation?: string; topK?: number; orgId?: string }) =>
      request<FirmBrainSearchResponse>('/alfie/firmbrain/briefed-before', { method: 'POST', body }),
    status: () => request<FirmBrainStatus>('/alfie/firmbrain/status'),
  },

  /** First run: the dashboard's first-steps checklist and the one-click fictional sample matter. */
  onboarding: {
    summary: () => request<OnboardingSummary>('/alfie/onboarding'),
    /** Close the checklist for good (server-side, per user). */
    dismiss: () => request<void>('/alfie/onboarding/dismiss', { method: 'POST' }),
    /** Idempotent: creates the sample matter once, then returns the existing one. */
    sampleMatter: () => request<SampleMatter>('/alfie/onboarding/sample-matter', { method: 'POST' }),
  },

  matters: {
    list: (page = 0, size = 20) =>
      request<Page<Matter>>('/alfie/matters', { query: { page, size } }),
    get: (id: string) => request<Matter>(`/alfie/matters/${id}`),
    create: (body: CreateMatterRequest) =>
      request<Matter>('/alfie/matters', { method: 'POST', body }),
    update: (id: string, body: UpdateMatterRequest) =>
      request<Matter>(`/alfie/matters/${id}`, { method: 'PATCH', body }),
    remove: (id: string) => request<void>(`/alfie/matters/${id}`, { method: 'DELETE' }),
    documents: (id: string, page = 0, size = 20) =>
      request<Page<GeneratedDocument>>(`/alfie/matters/${id}/documents`, { query: { page, size } }),
    conversations: (id: string, page = 0, size = 20) =>
      request<Page<Conversation>>(`/alfie/matters/${id}/conversations`, { query: { page, size } }),
    assignDocument: (id: string, documentId: string) =>
      request<void>(`/alfie/matters/${id}/documents/${documentId}`, { method: 'PUT' }),
    unassignDocument: (id: string, documentId: string) =>
      request<void>(`/alfie/matters/${id}/documents/${documentId}`, { method: 'DELETE' }),
    assignConversation: (id: string, conversationId: string) =>
      request<void>(`/alfie/matters/${id}/conversations/${conversationId}`, { method: 'PUT' }),
    unassignConversation: (id: string, conversationId: string) =>
      request<void>(`/alfie/matters/${id}/conversations/${conversationId}`, { method: 'DELETE' }),
    /** Upload case documents into a matter; they index into the user's matter-scoped facts. */
    uploadFacts: (id: string, files: File[]) =>
      request<FactDocument[]>(`/alfie/matters/${id}/facts`, { method: 'POST', body: filesToFormData(files) }),
    /** The case documents filed under a matter (distinct from generated documents). */
    facts: (id: string, page = 0, size = 20) =>
      request<Page<FactDocument>>(`/alfie/matters/${id}/facts`, { query: { page, size } }),

    // ---- Docket war room (roadmap §3.12) ----
    dockets: (id: string) => request<MatterDocket[]>(`/alfie/matters/${id}/dockets`),
    attachDocket: (id: string, body: AttachDocketRequest) =>
      request<MatterDocket>(`/alfie/matters/${id}/dockets`, { method: 'POST', body }),
    detachDocket: (id: string, docketId: string) =>
      request<void>(`/alfie/matters/${id}/dockets/${docketId}`, { method: 'DELETE' }),
    /** Keyset-paged newest-first timeline; pass the previous page's nextAfter as `after`. */
    docketEntries: (id: string, after?: string | null, size = 20) =>
      request<DocketEntriesPage>(`/alfie/matters/${id}/docket-entries`,
        { query: { after: after ?? undefined, size } }),
    matterDeadlines: (id: string, status?: string) =>
      request<MatterDeadline[]>(`/alfie/matters/${id}/deadlines`, { query: { status } }),
    confirmDeadline: (id: string, deadlineId: string) =>
      request<MatterDeadline>(`/alfie/matters/${id}/deadlines/${deadlineId}/confirm`, { method: 'POST' }),
    dismissDeadline: (id: string, deadlineId: string) =>
      request<MatterDeadline>(`/alfie/matters/${id}/deadlines/${deadlineId}/dismiss`, { method: 'POST' }),
    /** Hand-added deadlines (or saved calculator results); born CONFIRMED, duplicates skipped. */
    createDeadlines: (id: string, deadlines: { label: string; dueDate: string; ruleKey?: string }[]) =>
      request<MatterDeadline[]>(`/alfie/matters/${id}/deadlines`, { method: 'POST', body: { deadlines } }),
    /** Confirmed deadlines due within `days` across every matter the caller can see. */
    upcomingDeadlines: (days = 30) =>
      request<UpcomingDeadline[]>('/alfie/deadlines/upcoming', { query: { days } }),
    /** One matter's confirmed deadlines as an .ics file (Outlook / Apple Calendar / import anywhere). */
    deadlinesIcs: async (id: string) =>
      attachment(await request<Response>(`/alfie/matters/${id}/deadlines.ics`, { raw: true }), 'deadlines.ics'),
    /** Every confirmed deadline the caller can see, as an .ics file. */
    upcomingDeadlinesIcs: async (days = 365) =>
      attachment(await request<Response>('/alfie/deadlines/upcoming.ics', { raw: true, query: { days } }), 'alfie-deadlines.ics'),
  },

  deadlines: {
    /** Court-rules packs (V49): pick one, then fetch its rules. */
    packs: () => request<DeadlinePackView[]>('/alfie/deadlines/packs'),
    /** The calculator's dates as an .ics file — same inputs as `compute`, plus a calendar title. */
    exportIcs: async (body: { triggerDate: string; ruleKeys?: string[]; packKey?: string; title?: string }) =>
      attachment(await request<Response>('/alfie/deadlines/export', { method: 'POST', body, raw: true }), 'deadlines.ics'),
    rules: (pack?: string) => request<DeadlineRuleView[]>('/alfie/deadlines/rules', { query: { pack } }),
    compute: (triggerDate: string, ruleKeys?: string[], packKey?: string) =>
      request<DeadlineResult>('/alfie/deadlines/compute', { method: 'POST', body: { triggerDate, ruleKeys, packKey } }),
  },

  drafter: {
    create: (body: { posture: string; jurisdiction?: string; factPattern: string; matterId?: string }) =>
      request<DraftSessionDetail>('/alfie/drafter/sessions', { method: 'POST', body }),
    /** Start a draft already carrying vetted authority, pinned on every issue (all or nothing; RED refused). */
    seed: (body: DrafterSeedRequest) =>
      request<DraftSessionDetail>('/alfie/drafter/sessions/seeded', { method: 'POST', body }),
    /** With matterId: every draft filed under that matter (org-readable), not just the caller's. */
    list: (page = 0, size = 20, matterId?: string) =>
      request<Page<DraftSessionSummary>>('/alfie/drafter/sessions', { query: { page, size, matterId } }),
    get: (id: string) => request<DraftSessionDetail>(`/alfie/drafter/sessions/${id}`),
    /** Replace the session's issues: pass a list, or { suggest: true } for AI-proposed ones. */
    setIssues: (id: string, body: { issues?: string[]; suggest?: boolean }) =>
      request<DraftSessionDetail>(`/alfie/drafter/sessions/${id}/issues`, { method: 'POST', body }),
    /** Start generation (background job); poll the session for per-issue progress and api.jobs.poll for completion. */
    generate: (id: string, review = true) =>
      request<JobView>(`/alfie/drafter/sessions/${id}/generate`, { method: 'POST', body: { review } }),
    /** Retrieve + store the vetted authorities for one issue without drafting (unpinned). */
    suggestAuthorities: (id: string, issueId: string) =>
      request<DraftIssueView>(`/alfie/drafter/sessions/${id}/issues/${issueId}/authorities/suggest`, { method: 'POST' }),
    /** Replace (and pin) an issue's authority set; RED authority is refused by the server. */
    setAuthorities: (id: string, issueId: string, authorities: { sourceId?: string | null; citation?: string | null; title?: string | null }[]) =>
      request<DraftIssueView>(`/alfie/drafter/sessions/${id}/issues/${issueId}/authorities`, { method: 'PUT', body: { authorities } }),
    /** Redraft one section with instructions; lands in the document as a new revision. */
    regenerateIssue: (id: string, issueId: string, instructions?: string) =>
      request<JobView>(`/alfie/drafter/sessions/${id}/issues/${issueId}/regenerate`, {
        method: 'POST', body: { instructions: instructions || undefined },
      }),
    remove: (id: string) => request<void>(`/alfie/drafter/sessions/${id}`, { method: 'DELETE' }),
  },

  deconstructor: {
    /** Paste the opposing brief; analysis runs as a durable background job. */
    create: (body: { text: string; sourceName?: string; matterId?: string }) =>
      request<{ deconstruction: DeconstructionView; job: JobView }>('/alfie/deconstructor', { method: 'POST', body }),
    list: (page = 0, size = 20, matterId?: string) =>
      request<Page<DeconstructionSummary>>('/alfie/deconstructor', { query: { page, size, matterId } }),
    get: (id: string) => request<DeconstructionView>(`/alfie/deconstructor/${id}`),
    /** Seed a drafter session from the findings — the rebuttal funnel. */
    draft: (id: string) =>
      request<{ drafterSessionId: string }>(`/alfie/deconstructor/${id}/draft`, { method: 'POST' }),
    remove: (id: string) => request<void>(`/alfie/deconstructor/${id}`, { method: 'DELETE' }),
  },

  chronology: {
    build: (body: { text: string; focus?: string; title?: string }) =>
      request<ChronologyResponse>('/alfie/chronology', { method: 'POST', body }),
    buildDocuments: (files: File[], opts: { focus?: string; title?: string } = {}) =>
      request<ChronologyResponse>('/alfie/chronology/documents', {
        method: 'POST',
        body: filesToFormData(files, { focus: opts.focus, title: opts.title }),
      }),
    /** Build from a matter's indexed case documents — a background job; the result is filed under the matter. */
    buildFromMatter: (matterId: string, opts: { focus?: string; title?: string } = {}) =>
      request<JobView>(`/alfie/chronology/matters/${matterId}/async`, { method: 'POST', body: opts }),
    history: (page = 0, size = 20) =>
      request<Page<ChronologyResponse>>('/alfie/chronology/chronologies', { query: { page, size } }),
  },

  redaction: {
    redact: (body: { text: string; title?: string }) =>
      request<RedactionResponse>('/alfie/redaction', { method: 'POST', body }),
    redactDocuments: (files: File[], title?: string) =>
      request<RedactionResponse[]>('/alfie/redaction/documents', {
        method: 'POST',
        body: filesToFormData(files, { title }),
      }),
    history: (page = 0, size = 20) =>
      request<Page<RedactionResponse>>('/alfie/redaction/redactions', { query: { page, size } }),
  },

  /** Valorem — patent valuation (backend ValuationController, /alfie/valorem). */
  valorem: {
    /** Active intake field catalog, sectioned in wizard order (HIGH_LEVEL first). */
    fields: () => request<ValoremSectionView[]>('/alfie/valorem/fields'),
    /** Active scenarios plus their inline field definitions. */
    scenarios: () => request<ValoremScenario[]>('/alfie/valorem/scenarios'),

    createProfile: (body: ValoremCreateProfileRequest) =>
      request<ValoremProfile>('/alfie/valorem/profiles', { method: 'POST', body }),
    profiles: (opts: { matterId?: string; page?: number; size?: number } = {}) =>
      request<Page<ValoremProfile>>('/alfie/valorem/profiles', {
        query: { matterId: opts.matterId, page: opts.page ?? 0, size: opts.size ?? 20 },
      }),
    profile: (id: string) => request<ValoremProfile>(`/alfie/valorem/profiles/${id}`),
    updateProfile: (id: string, body: ValoremUpdateProfileRequest) =>
      request<ValoremProfile>(`/alfie/valorem/profiles/${id}`, { method: 'PATCH', body }),
    /** Partial `{fieldKey: value}` merge; `null` removes an answer. */
    patchAnswers: (id: string, answers: Record<string, unknown>) =>
      request<ValoremProfile>(`/alfie/valorem/profiles/${id}/answers`, { method: 'PATCH', body: answers }),
    /**
     * Flip per-answer AI consent (plan §14; stored on the answers-envelope
     * entry, so the answer must exist — the backend 400s otherwise).
     */
    setAnswerShare: (id: string, fieldKey: string, aiShareable: boolean) =>
      request<ValoremProfile>(
        `/alfie/valorem/profiles/${id}/answers/${encodeURIComponent(fieldKey)}/share`,
        { method: 'PATCH', body: { aiShareable } },
      ),
    deleteProfile: (id: string) =>
      request<void>(`/alfie/valorem/profiles/${id}`, { method: 'DELETE' }),
    /** Prefill "model answers" for unanswered fields; returns which fields were prefilled. */
    prefill: (id: string, aiPass = true) =>
      request<ValoremPrefillSummary>(`/alfie/valorem/profiles/${id}/prefill`, {
        method: 'POST',
        query: { aiPass },
      }),

    upsertScenarioInput: (id: string, scenarioKey: string, answers: Record<string, unknown>) =>
      request<ValoremScenarioInput>(`/alfie/valorem/profiles/${id}/scenarios/${scenarioKey}`, {
        method: 'PUT',
        body: answers,
      }),
    scenarioInputs: (id: string) =>
      request<ValoremScenarioInput[]>(`/alfie/valorem/profiles/${id}/scenarios`),

    /** Start an evidence sweep (background job); empty sourceKeys = all ACTIVE sources. */
    sweep: (id: string, sourceKeys: string[] = []) =>
      request<JobView>(`/alfie/valorem/profiles/${id}/evidence/sweep`, {
        method: 'POST',
        body: { sourceKeys },
      }),
    evidenceRuns: (id: string, page = 0, size = 20) =>
      request<Page<ValoremEvidenceRun>>(`/alfie/valorem/profiles/${id}/evidence/runs`, {
        query: { page, size },
      }),
    evidenceRecords: (id: string, opts: { source?: string; page?: number; size?: number } = {}) =>
      request<Page<ValoremEvidenceRecord>>(`/alfie/valorem/profiles/${id}/evidence/records`, {
        query: { source: opts.source, page: opts.page ?? 0, size: opts.size ?? 20 },
      }),

    /** Start a valuation run (background job); poll via api.jobs.poll. */
    startRun: (id: string, body: ValoremStartRunRequest = {}) =>
      request<JobView>(`/alfie/valorem/profiles/${id}/runs`, { method: 'POST', body }),
    runs: (opts: { profileId?: string; page?: number; size?: number } = {}) =>
      request<Page<ValoremRun>>('/alfie/valorem/runs', {
        query: { profileId: opts.profileId, page: opts.page ?? 0, size: opts.size ?? 20 },
      }),
    /** The run plus its prediction rows — progressive while the job is RUNNING. */
    run: (id: string) => request<ValoremRunDetail>(`/alfie/valorem/runs/${id}`),
    /** The run's composed report; 404 until the job wrote it. */
    report: (id: string) => request<ValoremReport>(`/alfie/valorem/runs/${id}/report`),
    /** Flat prediction history across runs, with optional filters. */
    predictions: (opts: { profileId?: string; scenario?: string; model?: string; page?: number; size?: number } = {}) =>
      request<Page<ValoremPrediction>>('/alfie/valorem/predictions', {
        query: {
          profileId: opts.profileId,
          scenario: opts.scenario,
          model: opts.model,
          page: opts.page ?? 0,
          size: opts.size ?? 20,
        },
      }),
    /** ACTIVE models for the run form, with the registry's DEGRADED state. */
    models: () => request<ValoremModel[]>('/alfie/valorem/models'),
  },

  discovery: {
    providers: () => request<ProviderSummary[]>('/ai/providers'),
    models: () => request<ModelSummary[]>('/ai/models'),
    chains: () => request<ChainSummary[]>('/ai/chains'),
  },

  admin: {
    /** Court-rules packs: import/edit a whole pack as one JSON document. */
    deadlinePacks: {
      get: (key: string) => request<DeadlinePackDetail>(`/admin/deadlines/packs/${key}`),
      upsert: (key: string, body: DeadlinePackUpsert) =>
        request<DeadlinePackDetail>(`/admin/deadlines/packs/${key}`, { method: 'PUT', body }),
      remove: (key: string) => request<void>(`/admin/deadlines/packs/${key}`, { method: 'DELETE' }),
    },
    users: {
      list: (page = 0, size = 20) => request<Page<UserResponse>>('/admin/users', { query: { page, size } }),
      get: (id: string) => request<UserResponse>(`/admin/users/${id}`),
      setRoles: (id: string, roles: Role[]) =>
        request<UserResponse>(`/admin/users/${id}/roles`, { method: 'PATCH', body: { roles } }),
      setStatus: (id: string, status: string) =>
        request<UserResponse>(`/admin/users/${id}/status`, { method: 'PATCH', body: { status } }),
    },
    audit: {
      list: (query: { userId?: string; action?: string; page?: number; size?: number }) =>
        request<Page<AuditEntry>>('/admin/audit', { query }),
    },
    ai: {
      providers: () => request<ProviderView[]>('/admin/ai/providers'),
      createProvider: (body: CreateProviderRequest) =>
        request<ProviderView>('/admin/ai/providers', { method: 'POST', body }),
      updateProvider: (key: string, body: UpdateProviderRequest) =>
        request<ProviderView>(`/admin/ai/providers/${key}`, { method: 'PATCH', body }),
      deleteProvider: (key: string) =>
        request<void>(`/admin/ai/providers/${key}`, { method: 'DELETE' }),
      health: (key: string) => request<HealthView>(`/admin/ai/providers/${key}/health`),
      /** Sends a real (tiny) chat completion through the engine end-to-end. */
      test: (key: string) => request<ProviderTestResult>(`/admin/ai/providers/${key}/test`, { method: 'POST' }),
      models: (providerId?: string) => request<ModelView[]>('/admin/ai/models', { query: { providerId } }),
      availableModels: (key: string, refresh = false) =>
        request<AvailableModel[]>(`/admin/ai/providers/${key}/available-models`, { query: { refresh } }),
      createModel: (body: CreateModelRequest) =>
        request<ModelView>('/admin/ai/models', { method: 'POST', body }),
      updateModel: (id: string, body: UpdateModelRequest) =>
        request<ModelView>(`/admin/ai/models/${id}`, { method: 'PATCH', body }),
      deleteModel: (id: string) =>
        request<void>(`/admin/ai/models/${id}`, { method: 'DELETE' }),
      pullModel: (id: string) =>
        request<PullStatus>(`/admin/ai/models/${id}/pull`, { method: 'POST' }),
      pullStatus: (id: string) =>
        request<PullStatus>(`/admin/ai/models/${id}/pull`),
      modelsReadiness: (providerId?: string) =>
        request<ModelReadiness[]>('/admin/ai/models/readiness', { query: { providerId } }),
      chains: () => request<ChainView[]>('/admin/ai/chains'),
      runChain: (key: string, body: { userInput: string; context?: string }) =>
        request<ChainResult>(`/admin/ai/chains/${key}/run`, { method: 'POST', body }),
    },
    citator: {
      /** Staff-only: classify treatment for every unclassified edge in the corpus (background job). */
      classifyAll: () =>
        request<JobView>('/admin/citator/classify-all', { method: 'POST' }),
    },
    library: {
      sources: (page = 0, size = 20) =>
        request<Page<SourceView>>('/admin/library/sources', { query: { page, size } }),
      source: (id: string) => request<SourceView>(`/admin/library/sources/${id}`),
      reindex: (id: string) =>
        request<Record<string, unknown>>(`/admin/library/sources/${id}/reindex`, { method: 'POST' }),
      remove: (id: string) =>
        request<Record<string, unknown>>(`/admin/library/sources/${id}`, { method: 'DELETE' }),
    },
    layers: {
      list: () => request<LayerView[]>('/admin/layers'),
      updateLayer: (key: string, status: string) =>
        request<LayerView>(`/admin/layers/${key}`, { method: 'PATCH', body: { status } }),
      toolLayers: (toolKey: string) => request<LayerConfig[]>(`/admin/layers/tools/${toolKey}/layers`),
      setToolLayer: (toolKey: string, layerKey: string, body: Partial<LayerConfig> & { enabled?: boolean; orderIndex?: number; defaultOn?: boolean }) =>
        request<LayerConfig>(`/admin/layers/tools/${toolKey}/layers/${layerKey}`, { method: 'PUT', body }),
      assets: (key: string) => request<LayerAsset[]>(`/admin/layers/${key}/assets`),
      sync: () => request<SyncResult>('/admin/layers/sync', { method: 'POST' }),
    },
    content: {
      forms: () => request<FormView[]>('/admin/content/forms'),
      form: (key: string) => request<FormView>(`/admin/content/forms/${key}`),
      createForm: (body: { key: string; name: string; schemaJson: string }) =>
        request<FormView>('/admin/content/forms', { method: 'POST', body }),
      updateForm: (key: string, body: { name?: string; schemaJson?: string; status?: string }) =>
        request<FormView>(`/admin/content/forms/${key}`, { method: 'PUT', body }),
      deleteForm: (key: string) => request<void>(`/admin/content/forms/${key}`, { method: 'DELETE' }),
      templates: () => request<TemplateView[]>('/admin/content/templates'),
      template: (key: string) => request<TemplateView>(`/admin/content/templates/${key}`),
      createTemplate: (body: { key: string; name: string; kind: string; content: string }) =>
        request<TemplateView>('/admin/content/templates', { method: 'POST', body }),
      updateTemplate: (key: string, body: { name?: string; content?: string; status?: string }) =>
        request<TemplateView>(`/admin/content/templates/${key}`, { method: 'PUT', body }),
      deleteTemplate: (key: string) => request<void>(`/admin/content/templates/${key}`, { method: 'DELETE' }),
    },
    ingestion: {
      trigger: (body: { jobType: JobType; sourcePath: string; sourceType?: string }) =>
        request<IngestionJob>('/admin/ingestion/jobs', { method: 'POST', body }),
      jobs: (page = 0, size = 20) => request<Page<IngestionJob>>('/admin/ingestion/jobs', { query: { page, size } }),
      job: (id: string) => request<IngestionJob>(`/admin/ingestion/jobs/${id}`),
      items: (id: string) => request<IngestionItem[]>(`/admin/ingestion/jobs/${id}/items`),
    },
    /** Source connectors (backend AdminConnectorController, /admin/connectors; ADMIN|DEV). */
    connectors: {
      list: () => request<ConnectorView[]>('/admin/connectors'),
      update: (key: string, body: { label?: string; configJson?: string; status?: string }) =>
        request<ConnectorView>(`/admin/connectors/${key}`, { method: 'PUT', body }),
      /** Starts a sync as a durable background job; poll with api.jobs.poll. */
      sync: (key: string, maxDocuments?: number) =>
        request<JobView>(`/admin/connectors/${key}/sync`, {
          method: 'POST',
          body: maxDocuments ? { maxDocuments } : {},
        }),
      resetCursor: (key: string) =>
        request<ConnectorView>(`/admin/connectors/${key}/reset-cursor`, { method: 'POST' }),
      runs: (key: string, page = 0, size = 20) =>
        request<Page<ConnectorRunView>>(`/admin/connectors/${key}/runs`, { query: { page, size } }),
    },
    /** Valorem control plane (backend AdminValuationController, /admin/valorem; ADMIN|DEV). */
    valorem: {
      // ---- intake fields
      fields: () => request<ValoremAdminField[]>('/admin/valorem/fields'),
      createField: (body: ValoremCreateFieldRequest) =>
        request<ValoremAdminField>('/admin/valorem/fields', { method: 'POST', body }),
      updateField: (id: string, body: ValoremUpdateFieldRequest) =>
        request<ValoremAdminField>(`/admin/valorem/fields/${id}`, { method: 'PATCH', body }),
      deleteField: (id: string) =>
        request<void>(`/admin/valorem/fields/${id}`, { method: 'DELETE' }),
      /** Rewrites positions to the given id order (spaced by 10). */
      reorderFields: (orderedIds: string[]) =>
        request<ValoremAdminField[]>('/admin/valorem/fields/reorder', {
          method: 'PATCH',
          body: { orderedIds },
        }),
      // ---- scenarios
      scenarios: () => request<ValoremAdminScenario[]>('/admin/valorem/scenarios'),
      createScenario: (body: ValoremCreateScenarioRequest) =>
        request<ValoremAdminScenario>('/admin/valorem/scenarios', { method: 'POST', body }),
      updateScenario: (id: string, body: ValoremUpdateScenarioRequest) =>
        request<ValoremAdminScenario>(`/admin/valorem/scenarios/${id}`, { method: 'PATCH', body }),
      deleteScenario: (id: string) =>
        request<void>(`/admin/valorem/scenarios/${id}`, { method: 'DELETE' }),
      // ---- evidence sources
      sources: () => request<ValoremEvidenceSource[]>('/admin/valorem/sources'),
      createSource: (body: ValoremCreateSourceRequest) =>
        request<ValoremEvidenceSource>('/admin/valorem/sources', { method: 'POST', body }),
      updateSource: (key: string, body: ValoremUpdateSourceRequest) =>
        request<ValoremEvidenceSource>(`/admin/valorem/sources/${key}`, { method: 'PATCH', body }),
      deleteSource: (key: string) =>
        request<void>(`/admin/valorem/sources/${key}`, { method: 'DELETE' }),
      /** Live probe: reachability, latency, sample record count. Always answers. */
      testSource: (key: string) =>
        request<ValoremSourceTest>(`/admin/valorem/sources/${key}/test`, { method: 'POST' }),
      // ---- weight profiles
      weights: () => request<ValoremWeightProfile[]>('/admin/valorem/weights'),
      createWeight: (body: ValoremCreateWeightProfileRequest) =>
        request<ValoremWeightProfile>('/admin/valorem/weights', { method: 'POST', body }),
      updateWeight: (id: string, body: ValoremUpdateWeightProfileRequest) =>
        request<ValoremWeightProfile>(`/admin/valorem/weights/${id}`, { method: 'PATCH', body }),
      deleteWeight: (id: string) =>
        request<void>(`/admin/valorem/weights/${id}`, { method: 'DELETE' }),
      /** Tuning starts from a copy. */
      duplicateWeight: (id: string) =>
        request<ValoremWeightProfile>(`/admin/valorem/weights/${id}/duplicate`, { method: 'POST' }),
      // ---- models + trainings
      models: () => request<ValoremAdminModel[]>('/admin/valorem/models'),
      createModel: (body: ValoremCreateModelRequest) =>
        request<ValoremAdminModel>('/admin/valorem/models', { method: 'POST', body }),
      updateModel: (id: string, body: ValoremUpdateModelRequest) =>
        request<ValoremAdminModel>(`/admin/valorem/models/${id}`, { method: 'PATCH', body }),
      deleteModel: (id: string) =>
        request<void>(`/admin/valorem/models/${id}`, { method: 'DELETE' }),
      /** Train a model in the background; poll via api.jobs.poll. */
      train: (id: string) =>
        request<JobView>(`/admin/valorem/models/${id}/train`, { method: 'POST' }),
      trainings: (id: string, page = 0, size = 20) =>
        request<Page<ValoremTraining>>(`/admin/valorem/models/${id}/trainings`, { query: { page, size } }),
      /** Pins active_training_id; only SUCCEEDED trainings qualify. */
      activateTraining: (id: string, trainingId: string) =>
        request<ValoremAdminModel>(`/admin/valorem/models/${id}/activate-training/${trainingId}`, {
          method: 'POST',
        }),
      // ---- comparables
      importComparables: () =>
        request<JobView>('/admin/valorem/comparables/import', { method: 'POST' }),
      importRuns: (page = 0, size = 20) =>
        request<Page<ValoremImportRun>>('/admin/valorem/comparables/imports', { query: { page, size } }),
      comparablesRecords: (opts: { category?: string; resolution?: string; page?: number; size?: number } = {}) =>
        request<Page<ValoremSettlementRecord>>('/admin/valorem/comparables/records', {
          query: {
            category: opts.category,
            resolution: opts.resolution,
            page: opts.page ?? 0,
            size: opts.size ?? 20,
          },
        }),
      /** Sample stats per category × resolution with the population-prior overlay. */
      comparablesStats: () =>
        request<ValoremComparablesStats>('/admin/valorem/comparables/stats'),
      // ---- local training corpus
      reingestLocalCorpus: () =>
        request<JobView>('/admin/valorem/localcorpus/reingest', { method: 'POST' }),
      localCorpusStatus: () =>
        request<ValoremLocalCorpusStatus>('/admin/valorem/localcorpus/status'),
      // ---- cross-user run log
      runLog: (opts: { userId?: string; page?: number; size?: number } = {}) =>
        request<Page<ValoremRunLog>>('/admin/valorem/runs', {
          query: { userId: opts.userId, page: opts.page ?? 0, size: opts.size ?? 20 },
        }),
    },
  },
};

export type Api = typeof api;
